import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Grid
} from '@mui/material';
import {
  Check as CheckIcon,
  Star as StarIcon,
  Email as EmailIcon
} from '@mui/icons-material';
import { brandColors } from '../../theme/theme';
import { FILM_TIERS, FILM_TIER_NAMES, TIER_FEATURES, TIER_PRICING } from '../../lib/filmSubscriptionTiers';

/**
 * FilmPricingTable Component
 * 
 * Displays pricing tiers for Film subscription
 */
const FilmPricingTable = ({ currentTier, onUpgrade, processingTier = null }) => {
  const tiers = [
    {
      tier: FILM_TIERS.FREE,
      name: FILM_TIER_NAMES[FILM_TIERS.FREE],
      price: TIER_PRICING[FILM_TIERS.FREE],
      features: TIER_FEATURES[FILM_TIERS.FREE],
      color: '#9e9e9e',
      popular: false
    },
    {
      tier: FILM_TIERS.BASIC,
      name: FILM_TIER_NAMES[FILM_TIERS.BASIC],
      price: TIER_PRICING[FILM_TIERS.BASIC],
      features: TIER_FEATURES[FILM_TIERS.BASIC],
      color: '#1976d2',
      popular: false
    },
    {
      tier: FILM_TIERS.PROFESSIONAL,
      name: FILM_TIER_NAMES[FILM_TIERS.PROFESSIONAL],
      price: TIER_PRICING[FILM_TIERS.PROFESSIONAL],
      features: TIER_FEATURES[FILM_TIERS.PROFESSIONAL],
      color: brandColors.primary,
      popular: true
    },
    {
      tier: FILM_TIERS.ENTERPRISE,
      name: FILM_TIER_NAMES[FILM_TIERS.ENTERPRISE],
      price: TIER_PRICING[FILM_TIERS.ENTERPRISE],
      features: TIER_FEATURES[FILM_TIERS.ENTERPRISE],
      color: '#d32f2f',
      popular: false
    }
  ];

  const handleUpgradeClick = (tier) => {
    if (processingTier) {
      return;
    }
    if (tier === FILM_TIERS.ENTERPRISE) {
      // Contact sales for enterprise
      window.location.href = 'mailto:sales@greenlightanalytics.com?subject=Film%20Enterprise%20Inquiry';
    } else if (onUpgrade) {
      onUpgrade(tier);
    }
  };

  const isCurrentTier = (tier) => tier === currentTier;
  const isDowngrade = (tier) => {
    const tierOrder = [FILM_TIERS.FREE, FILM_TIERS.BASIC, FILM_TIERS.PROFESSIONAL, FILM_TIERS.ENTERPRISE];
    return tierOrder.indexOf(tier) < tierOrder.indexOf(currentTier);
  };
  const isProcessing = (tier) => processingTier === tier;

  return (
    <Grid container spacing={3}>
      {tiers.map((tierData) => (
        <Grid item xs={12} md={6} lg={3} key={tierData.tier}>
          <Card
            sx={{
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              position: 'relative',
              border: isCurrentTier(tierData.tier) 
                ? `3px solid ${tierData.color}`
                : tierData.popular
                  ? `2px solid ${tierData.color}`
                  : '1px solid #e0e0e0',
              boxShadow: tierData.popular ? 6 : 2,
              transform: tierData.popular ? 'scale(1.05)' : 'none',
              transition: 'all 0.3s ease',
              '&:hover': {
                transform: tierData.popular ? 'scale(1.07)' : 'scale(1.02)',
                boxShadow: 8
              }
            }}
          >
            {/* Popular Badge */}
            {tierData.popular && (
              <Box
                sx={{
                  position: 'absolute',
                  top: -12,
                  right: 20,
                  bgcolor: tierData.color,
                  color: 'white',
                  px: 2,
                  py: 0.5,
                  borderRadius: 2,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  boxShadow: 3
                }}
              >
                <StarIcon sx={{ fontSize: 16 }} />
                <Typography variant="caption" sx={{ fontWeight: 600 }}>
                  Most Popular
                </Typography>
              </Box>
            )}

            {/* Current Plan Badge */}
            {isCurrentTier(tierData.tier) && (
              <Box
                sx={{
                  position: 'absolute',
                  top: -12,
                  left: 20,
                  bgcolor: '#4caf50',
                  color: 'white',
                  px: 2,
                  py: 0.5,
                  borderRadius: 2,
                  boxShadow: 3
                }}
              >
                <Typography variant="caption" sx={{ fontWeight: 600 }}>
                  Current Plan
                </Typography>
              </Box>
            )}

            <CardContent sx={{ flexGrow: 1, p: 3, display: 'flex', flexDirection: 'column' }}>
              {/* Tier Name */}
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 700,
                  mb: 1,
                  color: tierData.color,
                  mt: tierData.popular || isCurrentTier(tierData.tier) ? 2 : 0
                }}
              >
                {tierData.name}
              </Typography>

              {/* Price */}
              <Box sx={{ mb: 3 }}>
                {tierData.price.monthly === null ? (
                  <Typography variant="h4" sx={{ fontWeight: 700 }}>
                    Custom
                  </Typography>
                ) : tierData.price.monthly === 0 ? (
                  <Typography variant="h4" sx={{ fontWeight: 700 }}>
                    Free
                  </Typography>
                ) : (
                  <>
                    <Typography variant="h3" component="span" sx={{ fontWeight: 700 }}>
                      ${tierData.price.monthly}
                    </Typography>
                    <Typography variant="body1" component="span" color="text.secondary">
                      /month
                    </Typography>
                    <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.5 }}>
                      or ${tierData.price.annual}/year (save ${tierData.price.monthly * 2})
                    </Typography>
                  </>
                )}
              </Box>

              {/* Features */}
              <List dense sx={{ flexGrow: 1, mb: 2 }}>
                {tierData.features.map((feature, index) => (
                  <ListItem key={index} sx={{ px: 0, py: 0.5 }}>
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      <CheckIcon sx={{ color: tierData.color, fontSize: 20 }} />
                    </ListItemIcon>
                    <ListItemText
                      primary={feature}
                      primaryTypographyProps={{
                        variant: 'body2',
                        sx: { fontSize: '0.875rem' }
                      }}
                    />
                  </ListItem>
                ))}
              </List>

              {/* CTA Button */}
              {isCurrentTier(tierData.tier) ? (
                <Button
                  variant="outlined"
                  fullWidth
                  disabled
                  sx={{
                    py: 1.5,
                    fontWeight: 600,
                    textTransform: 'none'
                  }}
                >
                  Current Plan
                </Button>
              ) : isDowngrade(tierData.tier) ? (
                <Button
                  variant="outlined"
                  fullWidth
                  disabled
                  sx={{
                    py: 1.5,
                    fontWeight: 600,
                    textTransform: 'none',
                    opacity: 0.5
                  }}
                >
                  Downgrade (Contact Support)
                </Button>
              ) : tierData.tier === FILM_TIERS.ENTERPRISE ? (
                <Button
                  variant="contained"
                  fullWidth
                  startIcon={<EmailIcon />}
                  onClick={() => handleUpgradeClick(tierData.tier)}
                  sx={{
                    py: 1.5,
                    bgcolor: tierData.color,
                    color: 'white',
                    fontWeight: 600,
                    textTransform: 'none',
                    '&:hover': {
                      bgcolor: tierData.color + 'dd'
                    }
                  }}
                >
                  Contact Sales
                </Button>
              ) : (
                <Button
                  variant="contained"
                  fullWidth
                  onClick={() => handleUpgradeClick(tierData.tier)}
                  disabled={isProcessing(tierData.tier)}
                  sx={{
                    py: 1.5,
                    bgcolor: tierData.color,
                    color: 'white',
                    fontWeight: 600,
                    textTransform: 'none',
                    boxShadow: 3,
                    '&:hover': {
                      bgcolor: tierData.color + 'dd',
                      boxShadow: 6
                    }
                  }}
                >
                  {isProcessing(tierData.tier) ? 'Processing…' : 'Upgrade Now'}
                </Button>
              )}
            </CardContent>
          </Card>
        </Grid>
      ))}
    </Grid>
  );
};

export default FilmPricingTable;

